import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'production' ? '/api/v1/' : 'http://localhost:8000/api/v1/');

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          // Note: Refresh endpoint not documented in API spec
          // For now, just redirect to login on token expiry
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/';
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
);

// Authentication API functions
export const authAPI = {
  // Sign up new user
  signup: async (username, password, passwordConfirm) => {
    try {
      const response = await api.post('/auth/signup/', {
        username,
        password,
        password_confirm: passwordConfirm
      });
      
      // Store tokens
      if (response.data.token && response.data.refresh_token) {
        localStorage.setItem('access_token', response.data.token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Login user
  login: async (username, password) => {
    try {
      const response = await api.post('/auth/login/', {
        username,
        password
      });
      
      // Store tokens
      if (response.data.token && response.data.refresh_token) {
        localStorage.setItem('access_token', response.data.token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Logout user
  logout: async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await api.post('/auth/logout/', {
          refresh_token: refreshToken
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens regardless of API response
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },

  // Get current user info from token
  getCurrentUser: () => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    
    try {
      // Decode JWT token (basic decode, not verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.user_id,
        username: payload.username
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
};

// Tags API functions
export const tagsAPI = {
  // Get all tags
  getAll: async () => {
    try {
      const response = await api.get('/tags/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create a new tag
  create: async (name, description = '', evaluationApproach = '') => {
    try {
      const response = await api.post('/tags/', {
        name,
        description,
        evaluation_approach: evaluationApproach
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get a specific tag with lines
  getById: async (id) => {
    try {
      const response = await api.get(`/tags/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update a tag
  update: async (id, name, description = '', evaluationApproach = '') => {
    try {
      const response = await api.put(`/tags/${id}/`, {
        name,
        description,
        evaluation_approach: evaluationApproach
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete a tag
  delete: async (id) => {
    try {
      await api.delete(`/tags/${id}/`);
      return true;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
};

// Lines API functions
export const linesAPI = {
  // Get all lines with optional filtering
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/lines/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new lines (single or multiple)
  create: async (tagId, lines) => {
    try {
      const linesArray = Array.isArray(lines) ? lines : [lines];
      
        // Single line creation returns minimal data, so we'll use bulk endpoint for consistency
        const response = await api.post('/lines/bulk/', {
          tag_id: tagId,
          lines: linesArray
        });
        return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get a specific line with performance history
  getById: async (id) => {
    try {
      const response = await api.get(`/lines/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update a line
  update: async (id, content) => {
    try {
      const response = await api.put(`/lines/${id}/`, { content });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete a line
  delete: async (id) => {
    try {
      await api.delete(`/lines/${id}/`);
      return true;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
};

// Threads API functions
export const threadsAPI = {
  // Create a new thread
  create: async (tagId, threadName, threadDescription = '', totalQuestions = null) => {
    try {
      const payload = {
        tag: tagId,
        thread_name: threadName
      };

      if (threadDescription !== '') {
        payload.thread_description = threadDescription;
      }

      if (Number.isInteger(totalQuestions)) {
        payload.total_questions = totalQuestions;
      }

      const response = await api.post('/learning/threads/', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get all threads
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/learning/threads/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get a specific thread
  getById: async (threadId) => {
    try {
      const response = await api.get(`/learning/threads/${threadId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update thread metadata
  update: async (threadId, updates) => {
    try {
      const response = await api.put(`/learning/threads/${threadId}/update/`, updates);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete a thread
  delete: async (threadId) => {
    try {
      await api.delete(`/learning/threads/${threadId}/delete/`);
      return true;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Submit a response to a thread
  respond: async (threadId, response, responseTimeSeconds = 0) => {
    try {
      const apiResponse = await api.post(`/learning/threads/${threadId}/respond/`, {
        response: response,
        response_time_seconds: responseTimeSeconds
      });
      return apiResponse.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get current question for a thread
  getCurrentQuestion: async (threadId) => {
    try {
      const response = await api.get(`/learning/threads/${threadId}/current-question/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get conversation history for a thread
  getConversation: async (threadId) => {
    try {
      const response = await api.get(`/learning/threads/${threadId}/conversation/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Pause a thread
  pause: async (threadId) => {
    try {
      const response = await api.post(`/learning/threads/${threadId}/pause/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Resume a thread
  resume: async (threadId) => {
    try {
      const response = await api.post(`/learning/threads/${threadId}/resume/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk update threads
  bulkUpdate: async (threadIds, updates) => {
    try {
      const response = await api.post('/learning/threads/bulk-update/', {
        thread_ids: threadIds,
        updates: updates
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get thread statistics
  getStats: async () => {
    try {
      const response = await api.get('/learning/threads/stats/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
};

// Analytics API functions
const formatAnalyticsError = (error, fallbackMessage) => {
  const status = error.response?.status;
  const data = error.response?.data;

  let message = fallbackMessage;
  if (typeof data === 'string') {
    message = data;
  } else if (data?.detail) {
    message = data.detail;
  } else if (data?.message) {
    message = data.message;
  } else if (data?.error) {
    message = data.error;
  } else if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
    message = data.non_field_errors.join(' ');
  } else if (error.message) {
    message = error.message;
  }

  const formattedError = new Error(message);
  formattedError.status = status;
  formattedError.data = data;
  return formattedError;
};

export const analyticsAPI = {
  // Get comprehensive analytics overview
  getOverview: async (days = 30, gameType = 'drill') => {
    try {
      console.log('Making request to /analytics/overview/ with days:', days, 'gameType:', gameType);
      const response = await api.get('/analytics/overview/', {
        params: { days, game_type: gameType }
      });
      console.log('Analytics overview response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Analytics overview error:', error);
      console.error('Error response:', error.response);
      throw formatAnalyticsError(error, 'Failed to load analytics overview.');
    }
  }
};

const normalizeOptionalString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalizedValue || null;
};

const normalizeResistanceLevel = (value) => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);
  if (Number.isNaN(parsedValue) || parsedValue < 0 || parsedValue > 10) {
    return null;
  }

  return parsedValue;
};

const buildOptionalDrillContextPayload = (drillContext = {}) => {
  const optionalPayload = {};
  const normalizedPlace = normalizeOptionalString(drillContext.place);
  const normalizedStage = normalizeOptionalString(drillContext.stage);
  const normalizedResistanceLevel = normalizeResistanceLevel(drillContext.resistanceLevel);

  if (normalizedPlace) {
    optionalPayload.place = normalizedPlace;
  }

  if (normalizedStage) {
    optionalPayload.stage = normalizedStage;
  }

  if (normalizedResistanceLevel !== null) {
    optionalPayload.resistance_level = normalizedResistanceLevel;
  }

  return optionalPayload;
};

// Drills API functions
export const drillsAPI = {
  getLLMModels: async () => {
    try {
      const response = await api.get('/drills/llm-models/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createSession: async ({
    tagId,
    gameType = 'drill',
    totalQuestions = 10,
    place = '',
    resistanceLevel = '',
    stage = '',
    selectedTags = [],
    llmModel = ''
  }) => {
    try {
      const payload = {
        tag_id: tagId,
        game_type: gameType,
        total_questions: totalQuestions,
        ...buildOptionalDrillContextPayload({
          place,
          resistanceLevel,
          stage
        })
      };

      if (llmModel) {
        payload.llm_model = llmModel;
      }

      if (gameType === 'cue_game' && selectedTags.length > 0) {
        payload.selected_tags = selectedTags;
      }

      const response = await api.post('/drills/sessions/', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  generateQuestions: async ({
    sessionId,
    numQuestions
  }) => {
    try {
      const response = await api.get('/drills/generate-questions/', {
        params: {
          session_id: sessionId,
          num_questions: numQuestions
        }
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  evaluateDrill: async ({
    sessionId,
    questionNumber,
    cue,
    userResponse,
    responseTimeSeconds = 0
  }) => {
    try {
      const response = await api.post('/drills/evaluate/', {
        session_id: sessionId,
        question_number: questionNumber,
        cue: cue,
        user_response: userResponse,
        response_time_seconds: responseTimeSeconds,
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Submit feedback for a question/cue
  // drillResponseId is optional (null for questions before user submits answer)
  // lineId and tagId are optional but recommended for better tracking
  submitQuestionFeedback: async (drillResponseId, questionCue, isPositive, textFeedback = '', lineId = null, tagId = null) => {
    try {
      const response = await api.post('/drills/feedback/question/', {
        drill_response_id: drillResponseId,
        question_cue: questionCue,
        line_id: lineId,
        tag_id: tagId,
        is_positive: isPositive,
        text_feedback: textFeedback
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Submit feedback for an evaluation/result
  // If drillResponseId is null, provide cue, userResponse, and lineId instead
  submitEvaluationFeedback: async (drillResponseId, isPositive, textFeedback = '', cue = null, userResponse = null, lineId = null) => {
    try {
      const payload = {
        is_positive: isPositive,
        text_feedback: textFeedback
      };
      
      if (drillResponseId) {
        payload.drill_response_id = drillResponseId;
      } else {
        payload.cue = cue;
        payload.user_response = userResponse;
        payload.line_id = lineId;
      }
      
      const response = await api.post('/drills/feedback/evaluation/', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
};

export default api;
